import { DatePipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuctionService } from '@core/services/auction.service';
import { AuthService } from '@core/services/auth.service';
import { ConvertionUtils } from '@core/utils/convertion-utils';
import { FormUtils } from '@core/utils/form-utils';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { FilterStatus } from '@shared/interfaces/search.interface';
import { PlatformService } from '@shared/services/platform-service.service';
import { SearchService } from '@shared/services/search.service';
import { Subscription } from 'rxjs';
import {
  AuctionPrice,
  AuctionView,
} from 'src/app/articles-manager/interfaces/auction.interface';
import { BidBoxHeaderComponent } from '../bid-box-header/bid-box-header.component';
import { BidBoxComponent } from '../bid-box/bid-box.component';

@Component({
  selector: 'app-bid-search-result',
  templateUrl: './bid-search-result.component.html',
  styleUrls: ['./bid-search-result.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgbDropdownToggle,
    NgbDropdownMenu,
    FontAwesomeModule,
    NgTemplateOutlet,
    NgIf,
    BidBoxComponent,
    BidBoxHeaderComponent,
    TranslateModule,
    NgbDropdown,
    NgbDropdownItem,
  ],
  host: {
    ngSkipHydration: 'true',
  },
})
@UntilDestroy()
export class BidSearchResultComponent implements OnInit, OnDestroy {
  faSort = faSort;

  @ContentChild('filter') filterTemplate: TemplateRef<any>;
  @Input() memorabidAuctions: Array<AuctionView> = [];
  @Input() marketplaceAuctions: Array<AuctionView> = [];
  @Input() memorabidAuctionsCount: number | null = null;
  @Input() marketplaceAuctionsCount: number | null = null;
  @Input() showMarketplaceFirst: boolean = false;
  @Input() orderOrigin: 'frontend' | 'backend' = 'frontend';
  @Input() isBidBoxLarge: boolean = false;
  @Input() idAuctionList: Array<number> = [];
  @Input() showHeader: boolean = true;
  @Input() startingAuctionNumber: number = 30;
  @Output('auctionStatusFilterChanged')
  auctionStatusFilterEvent: EventEmitter<{
    auctionStatusFilter: FilterStatus;
  }> = new EventEmitter<{
    auctionStatusFilter: FilterStatus;
  }>();
  @Output()
  showMoreRequested = new EventEmitter();
  auctionStatusFilter: number = FilterStatus.OnlyActive;

  subscription: Subscription;

  currentOrder = '';

  private _refreshTimer = null;
  private readonly _refreshTimerIntervalLogged = 2000;
  private readonly _refreshTimerIntervalNotLogged = 30_000;

  constructor(
    private readonly router: Router,
    readonly searchService: SearchService,
    private readonly auctionService: AuctionService,
    private readonly datePipe: DatePipe,
    private readonly authService: AuthService,
    private readonly platformService: PlatformService
  ) {
    this.auctionStatusFilter = this.searchService.filters.auctionStatus;

    this.subscription = this.searchService
      .listenFiltersChanges()
      .subscribe(() => {
        if (
          this.router.url === '/search-result' &&
          this.searchService.filters.auctionStatus === FilterStatus.All
        ) {
          this.auctionStatusFilter = FilterStatus.All;
        }
      });
  }

  ngOnInit(): void {
    this.clearRefreshTimer();

    this.marketplaceAuctionsCount =
      this.marketplaceAuctionsCount ?? this.marketplaceAuctions.length;
    this.memorabidAuctionsCount =
      this.memorabidAuctionsCount ?? this.memorabidAuctions.length;

    if (
      this.router.url.split('/')[1] === 'promoter' ||
      this.router.url.split('/')[1] === 'campaign' ||
      this.router.url.split('/')[1] === 'tags' ||
      this.router.url.split('/')[1] === 'seller' ||
      (this.router.url === '/search-result' &&
        this.searchService.filters.auctionStatus === FilterStatus.All)
    ) {
      this.auctionStatusFilter = FilterStatus.All;
    }
    this.startTimer();
  }

  getOrderButtonTranslation(): string {
    if (this.currentOrder === '') {
      return 'IDS_ORDERING';
    }
    return 'IDS_ORDER_BY';
  }

  showAll() {
    if (this.auctionStatusFilter !== FilterStatus.All) {
      this.auctionStatusFilter = FilterStatus.All;
      this.auctionStatusFilterEvent.emit({
        auctionStatusFilter: this.auctionStatusFilter,
      });
    }
  }

  showActive() {
    if (this.auctionStatusFilter !== FilterStatus.OnlyActive) {
      this.auctionStatusFilter = FilterStatus.OnlyActive;
      this.auctionStatusFilterEvent.emit({
        auctionStatusFilter: this.auctionStatusFilter,
      });
    }
  }

  showClosed() {
    if (this.auctionStatusFilter !== FilterStatus.Closed) {
      this.auctionStatusFilter = FilterStatus.Closed;
      this.auctionStatusFilterEvent.emit({
        auctionStatusFilter: this.auctionStatusFilter,
      });
    }
  }

  showMoreClick() {
    this.showMoreRequested.emit();
  }

  /**
   * TODO: the commented code sends a request to the server to sort the auctions.
   * uncomment when pagination is implemented
   */
  orderBy(orderOption: number) {
    if (orderOption == 0) {
      this.currentOrder = 'IDS_EVIDENCE';
      if (this.orderOrigin === 'frontend') {
        this.memorabidAuctions.sort(
          (a, b) => b.evidenceLevel - a.evidenceLevel
        );
        this.marketplaceAuctions.sort(
          (a, b) => b.evidenceLevel - a.evidenceLevel
        );
        return;
      }
      this.searchService.setFilters(this.searchService.searchRoute, {
        ...this.searchService.filters,
        orderBy: 'evidence',
        orderDirection: 'desc',
      });
    } else if (orderOption === 1) {
      this.currentOrder = 'IDS_HIGHER_PRICE';
      if (this.orderOrigin === 'frontend') {
        this.memorabidAuctions.sort(
          (a, b) => this.maxPrice(b) - this.maxPrice(a)
        );
        this.marketplaceAuctions.sort(
          (a, b) => this.maxPrice(b) - this.maxPrice(a)
        );
        return;
      }
      this.searchService.setFilters(this.searchService.searchRoute, {
        ...this.searchService.filters,
        orderBy: 'price',
        orderDirection: 'desc',
      });
    } else if (orderOption === 2) {
      this.currentOrder = 'IDS_LOWER_PRICE';
      if (this.orderOrigin === 'frontend') {
        this.memorabidAuctions.sort(
          (a, b) => this.maxPrice(a) - this.maxPrice(b)
        );
        this.marketplaceAuctions.sort(
          (a, b) => this.maxPrice(a) - this.maxPrice(b)
        );
        return;
      }
      this.searchService.setFilters(this.searchService.searchRoute, {
        ...this.searchService.filters,
        orderBy: 'price',
        orderDirection: 'asc',
      });
    } else if (orderOption === 3) {
      this.currentOrder = 'IDS_MOST_RECENT_CLOSING_DATE';
      if (this.orderOrigin === 'frontend') {
        this.memorabidAuctions.sort((a, b) =>
          FormUtils.compareDates(
            ConvertionUtils.stringToDateTime(a.endDateView),
            ConvertionUtils.stringToDateTime(b.endDateView)
          )
        );
        this.marketplaceAuctions.sort((a, b) =>
          FormUtils.compareDates(
            ConvertionUtils.stringToDateTime(a.endDateView),
            ConvertionUtils.stringToDateTime(b.endDateView)
          )
        );
        return;
      }
      this.searchService.setFilters(this.searchService.searchRoute, {
        ...this.searchService.filters,
        orderBy: 'endDate',
        orderDirection: 'desc',
      });
    } else if (orderOption === 4) {
      this.currentOrder = 'IDS_EARLIEST_CLOSING_DATE';
      if (this.orderOrigin === 'frontend') {
        this.memorabidAuctions.sort((a, b) =>
          FormUtils.compareDates(
            ConvertionUtils.stringToDateTime(b.endDateView),
            ConvertionUtils.stringToDateTime(a.endDateView)
          )
        );
        this.marketplaceAuctions.sort((a, b) =>
          FormUtils.compareDates(
            ConvertionUtils.stringToDateTime(b.endDateView),
            ConvertionUtils.stringToDateTime(a.endDateView)
          )
        );
        return;
      }
      this.searchService.setFilters(this.searchService.searchRoute, {
        ...this.searchService.filters,
        orderBy: 'endDate',
        orderDirection: 'asc',
      });
    }
  }

  maxPrice(auction: AuctionView): number {
    if (
      auction.currentMaxOfferedPrice == null ||
      auction.currentMaxOfferedPrice === 0
    ) {
      return auction.basePrice;
    } else {
      return auction.currentMaxOfferedPrice;
    }
  }

  startTimer() {
    this.platformService.runOnBrowser(() => {
      if (!this._refreshTimer) {
        this._refreshTimer = setInterval(
          () => {
            this.updateAuctionPrice();
          },
          this.authService.isAuthenticated()
            ? this._refreshTimerIntervalLogged
            : this._refreshTimerIntervalNotLogged
        );
      }
    });
  }

  updateAuctionPrice() {
    if (this.idAuctionList.length > 0) {
      this.auctionService
        .getCurrentPrices(this.idAuctionList)
        .pipe(untilDestroyed(this))
        .subscribe((dataR) => {
          const dataMap = dataR.reduce((acc, item) => {
            acc[item.idAuction] = item;
            return acc;
          }, {} as { [key: number]: AuctionPrice });

          const updateAuctionPrice = (auction: AuctionView) => {
            const a = dataMap[auction.idAuction];
            if (a && auction.basePrice !== a.currentMaxOfferedPrice) {
              auction.currentMaxOfferedPrice = a.currentMaxOfferedPrice;
              auction.hasUserBided = a?.hasUserBided ?? auction.hasUserBided;
              auction.isUserBestBider =
                a?.isUserBestBider ?? auction.isUserBestBider;
              auction.userMaxOfferedPrice =
                a?.userMaxOfferedPrice ?? auction.userMaxOfferedPrice;
              auction.reservePriceReached =
                a?.reservePriceReached ?? auction.reservePriceReached;
              auction.endDateView = this.datePipe.transform(
                a.endDateView,
                'yyyy-MM-ddTHH:mm',
                'UTC'
              );
            }
          };

          this.memorabidAuctions.forEach(updateAuctionPrice);
          this.marketplaceAuctions.forEach(updateAuctionPrice);
        });
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.clearRefreshTimer();
  }

  private clearRefreshTimer(): void {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }
}
